var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable @typescript-eslint/no-unused-vars */
import { memo, useEffect, useState } from 'react';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { NotificationState, NotificationType } from 'root/src/shared/notification/notification.types';
import { ModalLayout } from 'root/src/client/v2/common/layouts/ModalLayout';
import Markdown from 'react-markdown';
import { useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { ButtonNew } from 'root/src/client/v2/common/components/form/ButtonNew';
import remarkGfm from 'remark-gfm';
import { useAuthentication } from 'root/src/client/v2/auth/hooks/useAuthentication';
const NotificationItem = memo((props) => {
    return (<div className="flex w-full flex-col gap-4">
			<div className="max-h-96 rounded overflow-scroll">
				<div className="py-4 px-4 rounded bg-dark-2">
					<Markdown remarkPlugins={[remarkGfm]} components={{
            h1: (_a) => {
                var { node } = _a, nodeProps = __rest(_a, ["node"]);
                return (<h1 {...nodeProps} className="text-2xl font-bold">
									{nodeProps.children}
								</h1>);
            },
            h2: (_a) => {
                var { node } = _a, nodeProps = __rest(_a, ["node"]);
                return (<h2 {...nodeProps} className="text-xl font-bold">
									{nodeProps.children}
								</h2>);
            },
            h3: (_a) => {
                var { node } = _a, nodeProps = __rest(_a, ["node"]);
                return (<h3 {...nodeProps} className="text-lg font-bold">
									{nodeProps.children}
								</h3>);
            },
            h4: (_a) => {
                var { node } = _a, nodeProps = __rest(_a, ["node"]);
                return (<h4 {...nodeProps} className="text-base font-bold">
									{nodeProps.children}
								</h4>);
            },
            h5: (_a) => {
                var { node } = _a, nodeProps = __rest(_a, ["node"]);
                return (<h5 {...nodeProps} className="text-sm font-bold">
									{nodeProps.children}
								</h5>);
            },
            hr: (_a) => {
                var { node } = _a, nodeProps = __rest(_a, ["node"]);
                return (<hr {...nodeProps} className="border-t border-dark-1 my-4"/>);
            },
            a: (_a) => {
                var { node } = _a, nodeProps = __rest(_a, ["node"]);
                return (<a {...nodeProps} className="text-link underline" href={nodeProps.href} target="_blank" rel="noopener noreferrer">
									{nodeProps.children}
								</a>);
            },
            ol: (_a) => {
                var { node } = _a, nodeProps = __rest(_a, ["node"]);
                return (<ol {...nodeProps} className="list-decimal my-1 list-inside">
									{nodeProps.children}
								</ol>);
            },
            ul: (_a) => {
                var { node } = _a, nodeProps = __rest(_a, ["node"]);
                return (<ul {...nodeProps} className="list-disc my-1 list-inside">
									{nodeProps.children}
								</ul>);
            },
        }}>
						{props.notification.content}
					</Markdown>
				</div>
			</div>
			<ButtonNew type="button" onClick={props.onClose}>
				{props.hasNext ? 'Next' : 'Close'}
			</ButtonNew>
		</div>);
});
const PopupNotificationsModal = memo((props) => {
    const modal = useModal();
    const readNotificationMutation = trpc.notification.read.useMutation();
    const [notificationIndex, setNotificationIndex] = useState(0);
    useEffect(() => {
        setNotificationIndex(0);
    }, [props.notifications]);
    const handleClose = (notification) => __awaiter(void 0, void 0, void 0, function* () {
        void readNotificationMutation.mutateAsync({
            notificationId: notification.id,
        });
        if (notificationIndex < props.notifications.length - 1) {
            setNotificationIndex(notificationIndex + 1);
            return;
        }
        modal.hide();
    });
    const notification = props.notifications[notificationIndex];
    if (!notification) {
        modal.hide();
        return null;
    }
    return (<ModalLayout className="sm:w-[620px] md:w-[700px]">
			<NotificationItem notification={notification} onClose={() => handleClose(notification)} hasNext={notificationIndex < props.notifications.length - 1}/>
		</ModalLayout>);
});
export const Notifications = memo(() => {
    const { authentication } = useAuthentication();
    const notificationsQuery = trpc.notification.list.useQuery({
        state: NotificationState.Unread,
        type: NotificationType.Popup,
    }, { enabled: Boolean(authentication) });
    const modal = useModal();
    const notifications = notificationsQuery.data || [];
    useEffect(() => {
        if (notifications.length) {
            modal.show(<PopupNotificationsModal notifications={notifications}/>, {
                isCloseable: false,
            });
        }
    }, [notifications]);
    return null;
});
